import api from './api';

export interface SubscriptionInfo {
  tier: string;
  paidTier?: string;
  status: string;
  nextBillingDate: string | null;
  cancelAtPeriodEnd?: boolean;
  amount: number;
  billingInterval: 'MONTHLY' | 'YEARLY';
  stripeCustomerId?: string | null;
  manualOverride?: {
    tier: string;
    expiresAt: string | null;
    grantedBy: string | null;
    grantedAt: string | null;
    reason: string | null;
  } | null;
  usageLimits: {
    players: { used: number; limit: number };
    leagues: { used: number; limit: number };
    storage: { used: number; limit: number };
  };
}

export interface Invoice {
  id: string;
  date: string;
  amount: number;
  status: string;
  description?: string;
  downloadUrl?: string;
}

export interface PlanInfo {
  id: string;
  name: string;
  monthlyPrice: number;
  yearlyPrice: number;
  features: string[];
  limits: {
    players: number;
    leagues: number;
    storage: number;
  };
}

export interface PaymentMethod {
  id: string;
  type: string;
  last4: string;
  brand: string;
  expiryMonth: number;
  expiryYear: number;
  isDefault: boolean;
}

export const billingService = {
  /**
   * Get current subscription information
   */
  async getSubscriptionInfo(orgSlug: string): Promise<SubscriptionInfo> {
    const response = await api.get(`/billing/${orgSlug}/subscription`);
    return response.data;
  },

  /**
   * Get invoice history
   */
  async getInvoices(organizationId: string): Promise<Invoice[]> {
    const response = await api.get(`/organizations/${organizationId}/invoices`);
    return response.data.invoices;
  },

  /**
   * Download invoice
   */
  async downloadInvoice(organizationId: string, invoiceId: string): Promise<Blob> {
    const response = await api.get(`/organizations/${organizationId}/invoices/${invoiceId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Get available plans
   */
  async getAvailablePlans(): Promise<PlanInfo[]> {
    try {
      const response = await api.get('/billing/plans');
      return response.data.plans;
    } catch {
      // Return default plans if API fails
      return [
        {
          id: 'STARTER',
          name: 'Starter',
          monthlyPrice: 0,
          yearlyPrice: 0,
          features: [
            'Schedule & calendar',
            'Auto league sync',
            'Venue maps',
            'League standings',
            'Conflict alerts',
            'Push notifications',
          ],
          limits: { players: 10, leagues: 10, storage: 500 },
        },
        {
          id: 'FAMILY',
          name: 'Family',
          monthlyPrice: 4.99,
          yearlyPrice: 40.10,
          features: [
            'Everything in Starter',
            'Smart conflict detection',
            'Weather forecasts',
            'Departure times & travel',
            'Multi-player conflicts',
            'Email digests & alerts',
            'League analytics',
          ],
          limits: { players: 10, leagues: 10, storage: 500 },
        },
      ];
    }
  },

  /**
   * Upgrade subscription
   */
  async upgradeSubscription(organizationId: string, planId: string): Promise<{ success: boolean; message?: string }> {
    const response = await api.post(`/organizations/${organizationId}/subscription/upgrade`, {
      planId,
    });
    return response.data;
  },

  /**
   * Downgrade subscription
   */
  async downgradeSubscription(organizationId: string, planId: string): Promise<{ success: boolean; message?: string }> {
    const response = await api.post(`/organizations/${organizationId}/subscription/downgrade`, {
      planId,
    });
    return response.data;
  },

  /**
   * Cancel subscription / downgrade to Starter
   */
  async cancelSubscription(orgSlug: string): Promise<{ success: boolean; message?: string }> {
    const response = await api.post(`/billing/${orgSlug}/downgrade-starter`);
    return response.data;
  },

  /**
   * Get payment methods
   */
  async getPaymentMethods(organizationId: string): Promise<PaymentMethod[]> {
    const response = await api.get(`/organizations/${organizationId}/payment-methods`);
    return response.data.paymentMethods;
  },

  /**
   * Add payment method
   */
  async addPaymentMethod(organizationId: string, paymentMethodId: string): Promise<{ success: boolean }> {
    const response = await api.post(`/organizations/${organizationId}/payment-methods`, {
      paymentMethodId,
    });
    return response.data;
  },

  /**
   * Set default payment method
   */
  async setDefaultPaymentMethod(organizationId: string, paymentMethodId: string): Promise<{ success: boolean }> {
    const response = await api.put(`/organizations/${organizationId}/payment-methods/${paymentMethodId}/default`);
    return response.data;
  },

  /**
   * Remove payment method
   */
  async removePaymentMethod(organizationId: string, paymentMethodId: string): Promise<{ success: boolean }> {
    const response = await api.delete(`/organizations/${organizationId}/payment-methods/${paymentMethodId}`);
    return response.data;
  },

  /**
   * Create Stripe setup intent for adding payment method
   */
  async createSetupIntent(organizationId: string): Promise<{ clientSecret: string }> {
    const response = await api.post(`/organizations/${organizationId}/setup-intent`);
    return response.data;
  },

  /**
   * Get usage statistics
   */
  async getUsageStats(organizationId: string): Promise<SubscriptionInfo['usageLimits']> {
    const response = await api.get(`/organizations/${organizationId}/usage`);
    return response.data.usage;
  },

  /**
   * Create Stripe checkout session
   */
  async createCheckoutSession(
    orgSlug: string,
    tier: string,
    billingInterval: 'MONTHLY' | 'YEARLY' = 'MONTHLY'
  ): Promise<{ sessionId: string; url: string }> {
    const response = await api.post(`/billing/${orgSlug}/checkout`, { tier, billingInterval });
    return response.data;
  },

  /**
   * Create Stripe billing portal session
   */
  async createPortalSession(orgSlug: string): Promise<{ url: string }> {
    const response = await api.post(`/billing/${orgSlug}/portal`);
    return response.data;
  },

  /**
   * Get billing history
   */
  async getBillingHistory(orgSlug: string): Promise<Invoice[]> {
    const response = await api.get(`/billing/${orgSlug}/history`);
    return response.data;
  },
};
